import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Typography, Box, Container } from '@material-ui/core';
import MotiveTitle from '../../../Contact/Motive/MotiveTitle';
import AppInput from '../../../../common/AppInput';
import ProductPreview from '../../../../common/ProductPreview';
import AppSelect from '../../../../common/AppSelect';
import Message from '../../../../common/Message';
import { COMPLETE_FORM } from '../../../../../constants/texts';
import { useCancelPurchaseStyles } from './styles';
import {
  debitCard,
  messageMapper,
  optionMapper,
} from '../../../../../helpers/tickets/PaymentTypeHelper';
import { textMapper } from '../../../../../helpers/FormHelper';
import { getMotiveOptions, motiveGroups } from './validation/fields';
import { resetPost } from '../../../../../redux/slices/ValidationProductSlice';
import DeadlineExpiredWithNoReverse from '../../../Issues/DeadlineExpiredWithNoReverse';
import Loader from '../../../../common/Loader';

const Form = ({ ...props }) => {
  const {
    state,
    onData,
    fields,
    mobile,
    onPaymentMethod,
    motiveId,
    setShowForm,
  } = props;
  const classes = useCancelPurchaseStyles({ mobile });
  const { sku } = useSelector((state) => state.issuesSlice);
  const { messages, status, innerMessage, getStatus } = useSelector(
    (state) => state.validationProductSlice,
  );
  const [motiveGroup, setMotiveGroup] = useState('');
  const reduxDispatch = useDispatch();

  const isMkp = (oc) => oc.includes('-');

  let loadingGif = getStatus === 'loading';

  const handleChange = (e) => {
    onData({ name: e.target.name, value: e.target.value });
  };

  const handleMotiveGroup = (e) => {
    setMotiveGroup(e.target.value);
    if (state['motiveClient']?.value) {
      onData({ name: 'motiveClient', value: '' });
    }
  };

  const selectedMotiveGroup = motiveGroups.find(
    (group) => group.id === motiveGroup,
  );

  const extraFields = ['accountNumber', 'bank', 'cancelAddress'];

  const hiddenInput = (field, state) => {
    return !extraFields.includes(field.name)
      ? false
      : state['paymentMethod']?.value !== debitCard
      ? true
      : false;
  };

  useEffect(() => {
    if (state['paymentMethod']?.value)
      onPaymentMethod(state['paymentMethod']?.value === debitCard ? 13 : 10);
  }, [state]);

  useEffect(() => {
    setShowForm(status);
  }, [status]);

  useEffect(() => {
    reduxDispatch(resetPost());
  }, [resetPost]);

  return (
    <Box data-testid="cancel-purchase-form">
      <MotiveTitle
        testId="cancel-purchase-title"
        title={textMapper.get(motiveId).title}
        subtitle={textMapper.get(motiveId).subtitle}
        icon={textMapper.get(motiveId).icon}
        mobile={mobile}
      />

      <ProductPreview mobile={mobile} testId="cancel-purchase-product" />

      {loadingGif && <Loader mobile={mobile} testId="cancel-purchase-loader" />}

      {!status &&
        messages &&
        innerMessage.includes('Product with credit note') && (
          <DeadlineExpiredWithNoReverse
            issueForm
            mobile={mobile}
            motiveId="8"
            typeId="2"
          />
        )}

      {(isMkp(sku) || status) && (
        <React.Fragment>
          <Typography className={classes.title}>Elige una opción:</Typography>
          <AppSelect
            testId="cancel-purchase-motive-group"
            label="Arrepentimiento"
            name="motiveGroup"
            options={motiveGroups}
            value={motiveGroup}
            onChange={handleMotiveGroup}
            onClick={() => {}}
            onBlur={() => {}}
          />
          {selectedMotiveGroup && (
            <React.Fragment>
              <Container>
                <Box>
                  <Message
                    testId="cancel-purchase-motive-info"
                    fullRounded
                    message={selectedMotiveGroup.description}
                    severity="info"
                  />
                </Box>
              </Container>
              <AppSelect
                testId="cancel-purchase-motive"
                label="Motivo del arrepentimiento"
                name="motiveClient"
                options={getMotiveOptions(selectedMotiveGroup)}
                value={state['motiveClient']?.value || ''}
                error={state['motiveClient']?.error || false}
                errorMessage={state['motiveClient']?.message || ''}
                onChange={handleChange}
                onClick={() => {}}
                onBlur={() => {}}
              />
            </React.Fragment>
          )}

          {status && (
            <Typography
              className={classes.title}
              data-testid="cancel-purchase-complete-form-title"
            >
              {COMPLETE_FORM}
            </Typography>
          )}

          {status &&
            fields.map((field, index) => (
              <React.Fragment key={index}>
                {!field.hidden && field.inputType !== 'select' && (
                  <AppInput
                    testId={`cancel-purchase-field-${field.name}`}
                    onChange={handleChange}
                    onBlur={() => {}}
                    onClick={() => {}}
                    disabled={state[field.name]?.disabled || false}
                    name={field.name}
                    error={state[field.name]?.error || false}
                    type={state[field.name]?.type || ''}
                    label={state[field.name]?.label || ''}
                    required={state[field.name]?.required || false}
                    inputAdornment={state[field.name]?.inputAdornment || ''}
                    success={state[field.name]?.success || ''}
                    helperText={state[field.name]?.helper || ''}
                    multiline={state[field.name]?.multiline || false}
                    rows={state[field.name]?.rows || ''}
                    value={state[field.name]?.value || ''}
                    errorMessage={state[field.name]?.message || ''}
                    hidden={hiddenInput(field, state)}
                  />
                )}
                {field.inputType === 'select' && (
                  <AppSelect
                    testId={`cancel-purchase-field-${field.name}`}
                    label={state[field.name]?.label || ''}
                    name={field.name}
                    options={optionMapper.get(field.name)}
                    onChange={handleChange}
                    required={state[field.name]?.required || false}
                    onClick={() => {}}
                    onBlur={() => {}}
                    hidden={hiddenInput(field, state)}
                  />
                )}
              </React.Fragment>
            ))}
          {status && state['paymentMethod']?.value && (
            <Box marginTop="32px">
              <Message
                testId="cancel-purchase-payment-info"
                fullRounded
                message={messageMapper.get(state['paymentMethod']?.value).msg}
                severity={
                  messageMapper.get(state['paymentMethod']?.value).severity
                }
              />
            </Box>
          )}
        </React.Fragment>
      )}
    </Box>
  );
};

Form.propTypes = {
  state: PropTypes.object,
  onData: PropTypes.func,
  fields: PropTypes.array,
  mobile: PropTypes.bool,
  onPaymentMethod: PropTypes.func,
  motiveId: PropTypes.string,
  setShowForm: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
};

export default Form;
